import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { X, Star } from "lucide-react";
import { IsaiahMascotCore } from "./IsaiahMascotCore";
import { useIsaiah } from "./IsaiahPlacementManager";
import { getContextMessage, WORLD_ROLES } from "./IsaiahAnimationLibrary";
import { useApp } from "../../context";
import { useFabsCollapsed } from "../../lib/fabTidy";
import { useDraggableFab } from "../../lib/useDraggableFab";
import "./isaiah-animations.css";

// When the user taps the X, we simply minimize Isaiah (the mascot hides and the
// small star chat button remains). Tapping the star chat button brings Isaiah
// back — no timed auto-hide. Persisted so it survives navigation.
const MIN_KEY = "isaiah_companion_min";

/**
 * IsaiahFloatingCompanion v3
 * World-aware floating helper. In learning spaces, offers hints.
 * In arcade/adventure/creator, adopts world-specific pose and personality.
 */
const IsaiahFloatingCompanion = () => {
  const { user } = useApp() || {};
  const {
    pageContext,
    worldId,
    isCompanionVisible,
    isCompanionMinimized,
    isCompanionHidden,
    companionMessage,
    dismissCompanion,
    restoreCompanion,
    companionPose,
    isTransitioning,
  } = useIsaiah();

  const location = useLocation();
  const isGamePage = location.pathname.startsWith("/learn/") || location.pathname.startsWith("/arcade/");
  const fabsCollapsed = useFabsCollapsed();
  const starDrag = useDraggableFab("iw_fab_pos_star", 176);

  const [showBubble, setShowBubble] = useState(false);
  const [bubbleText, setBubbleText] = useState("");
  const [exiting, setExiting] = useState(false);
  const bubbleTimer = useRef(null);
  const isLearningContext = pageContext === "learn" || pageContext === "learn-activity";
  const worldRole = worldId ? WORLD_ROLES[worldId] : null;

  // Determine pose based on world context
  const getCompanionPose = () => {
    if (companionPose !== "idle") return companionPose;
    if (isLearningContext) return "reading";
    return "idle";
  };

  // Determine motion state based on world context
  const getCompanionMotion = () => {
    if (worldId === "arcade") return "hype";
    if (worldId === "adventure") return "exploring";
    if (worldId === "creator") return "crafting";
    if (isLearningContext) return "idle";
    return "idle";
  };

  // Guest-friendly messages for unauthenticated homepage visitors
  // Updated per user preference: keep it Isaiah's-World-branded but drop the
  // "I'm Isaiah" first-person framing now that the site has a narrator voice.
  const GUEST_HOME_MESSAGES = [
    "Hey there, explorer! Welcome to Isaiah's World, want a quick tour?",
    "Welcome to Isaiah's World! There's so much to discover here.",
    "Hey! Ready for an amazing adventure across Isaiah's World?",
    "Come explore, learn, and play in Isaiah's World!",
  ];

  const getMessageForContext = (ctx) => {
    if (!user && ctx === "home") {
      return GUEST_HOME_MESSAGES[Math.floor(Math.random() * GUEST_HOME_MESSAGES.length)];
    }
    return getContextMessage(ctx);
  };

  const greetedRef = useRef(false);

  // Show greeting message when companion first becomes visible, ONCE.
  // Previously this re-fired whenever `user` resolved async from API,
  // causing the speech bubble text to flip mid-animation and look like a flash.
  useEffect(() => {
    if (greetedRef.current) return;
    if (isCompanionVisible && !isCompanionHidden) {
      greetedRef.current = true;
      const t = setTimeout(() => {
        const msg = getMessageForContext(pageContext);
        setBubbleText(msg);
        setShowBubble(true);
        bubbleTimer.current = setTimeout(() => setShowBubble(false), 6000);
      }, 1500);
      return () => {
        clearTimeout(t);
        if (bubbleTimer.current) clearTimeout(bubbleTimer.current);
      };
    }
  }, [pageContext, isCompanionVisible, isCompanionHidden]);

  // Reset the greeted flag when navigating between contexts so kids get
  // a context-appropriate welcome on each new page.
  useEffect(() => { greetedRef.current = false; }, [pageContext]);

  // Show external messages
  useEffect(() => {
    if (companionMessage) {
      setBubbleText(companionMessage);
      setShowBubble(true);
      if (bubbleTimer.current) clearTimeout(bubbleTimer.current);
      bubbleTimer.current = setTimeout(() => setShowBubble(false), 5000);
    }
  }, [companionMessage]);

  const handleClick = useCallback(() => {
    if (isCompanionMinimized && !isCompanionVisible) {
      restoreCompanion();
      return;
    }
    const msg = getMessageForContext(pageContext);
    setBubbleText(msg);
    setShowBubble(true);
    if (bubbleTimer.current) clearTimeout(bubbleTimer.current);
    bubbleTimer.current = setTimeout(() => setShowBubble(false), 5000);
  }, [pageContext, isCompanionMinimized, isCompanionVisible, restoreCompanion, user]);

  const handleDismiss = useCallback((e) => {
    e.stopPropagation();
    setExiting(true);
    try { localStorage.setItem(MIN_KEY, "1"); } catch {}
    setTimeout(() => {
      setUserMin(true);
      setExiting(false);
      setShowBubble(false);
    }, 300);
  }, []);

  // When a bottom-right popup is on screen (ProfileCompletionToast,
  // SoloExplorerProfileNudge, UpgradeNudge, InstallPrompt, the Lawrence
  // voice welcome, etc.) we no longer HIDE Isaiah, instead we lift him UP
  // to sit just above the popup so his buttons stay tappable, then drop him
  // back to his home spot when the popup closes. We poll the DOM every 500ms
  // and measure the highest popup so this self-heals as popups come and go.
  const [popupLiftPx, setPopupLiftPx] = useState(0);
  // User minimize: tapping the X hides the mascot; the star chat button stays.
  // Tapping that chat button (which fires "isaiah:restore-companion") brings
  // Isaiah back. No timed auto-hide.
  const [userMin, setUserMin] = useState(() => {
    try { return localStorage.getItem(MIN_KEY) === "1"; } catch { return false; }
  });
  useEffect(() => {
    const restore = () => {
      try { localStorage.removeItem(MIN_KEY); } catch {}
      setUserMin(false);
    };
    window.addEventListener("isaiah:restore-companion", restore);
    return () => window.removeEventListener("isaiah:restore-companion", restore);
  }, []);
  useEffect(() => {
    const SELECTORS = [
      "[data-testid='profile-completion-toast']",
      "[data-testid='solo-explorer-profile-nudge']",
      "[data-testid='upgrade-nudge']",
      "[data-testid='install-prompt']",
      "[data-testid='home-welcome-card']",
    ];
    const check = () => {
      let highestTop = Infinity;
      for (const sel of SELECTORS) {
        const el = document.querySelector(sel);
        if (!el) continue;
        const r = el.getBoundingClientRect();
        if (r.width > 0 && r.height > 0 && r.top < highestTop) highestTop = r.top;
      }
      if (highestTop === Infinity) { setPopupLiftPx(0); return; }
      // Place Isaiah's bottom edge ~12px above the popup's top edge.
      const lift = Math.max(0, window.innerHeight - highestTop + 12);
      setPopupLiftPx(lift);
    };
    check();
    const id = setInterval(check, 500);
    window.addEventListener("resize", check);
    return () => { clearInterval(id); window.removeEventListener("resize", check); };
  }, []);
  // Only lift on tablet/desktop; on small phones the corner is too tight, so
  // fall back to hiding when a popup is up.
  const isNarrow = typeof window !== "undefined" && window.innerWidth < 640;
  const liftStyle = popupLiftPx > 0 && !isNarrow ? { bottom: `${popupLiftPx}px` } : undefined;

  // Consolidated to a single site-wide helper button (see NPCGuide). The
  // separate floating mascot is disabled so only ONE Isaiah circle floats.
  return null;
  // eslint-disable-next-line no-unreachable
  if (userMin) return null;
  if (isCompanionHidden || isGamePage || fabsCollapsed || (popupLiftPx > 0 && isNarrow)) return null;

  // Minimized state: small star button with world-colored accent
  if (isCompanionMinimized && !isCompanionVisible) {
    const btnColor = worldRole?.color || "rgb(37,99,235)";
    return (
      <button
        onClick={() => { if (starDrag.shouldSwallowClick()) return; restoreCompanion(); }}
        {...starDrag.bind}
        className="fixed z-[60] w-10 h-10 rounded-full border flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
        style={{
          backgroundColor: `${btnColor}CC`,
          borderColor: `${btnColor}50`,
          animation: "isaiah-companion-bob 3s ease-in-out infinite",
          ...(liftStyle || {}),
          ...starDrag.style,
        }}
        data-testid="isaiah-companion-minimized"
        aria-label="Show Isaiah helper"
      >
        <Star size={18} className="text-white" fill="currentColor" />
      </button>
    );
  }

  // World-colored speech bubble border
  const bubbleBorder = worldRole ? `${worldRole.color}25` : "rgba(255,255,255,0.15)";
  const roleLabel = worldRole?.title;

  // Determine enter/exit animation class
  const getAnimationClass = () => {
    if (exiting) return "isaiah-companion-exit";
    if (isTransitioning) return `isaiah-world-exit-${worldId || "default"}`;
    return "isaiah-companion-enter";
  };

  return (
    <div
      {...starDrag.bind}
      className={`fixed z-[60] transition-[bottom] duration-300 ${getAnimationClass()}`}
      style={{ ...(liftStyle || {}), ...starDrag.style }}
      data-testid="isaiah-floating-companion"
    >
      {/* Speech bubble */}
      {showBubble && bubbleText && (
        <div className="absolute bottom-full right-0 mb-2 isaiah-speech-enter" data-testid="isaiah-companion-bubble">
          <div className="relative max-w-[200px] rounded-xl rounded-br-sm bg-black/70 px-3 py-2 shadow-2xl" style={{ borderWidth: 1, borderColor: bubbleBorder }}>
            {roleLabel && (
              <p className="text-[8px] font-bold uppercase tracking-wider mb-0.5" style={{ color: worldRole.color }}>{roleLabel} Isaiah</p>
            )}
            <p className="text-white/90 text-[11px] leading-relaxed font-nunito">{bubbleText}</p>
            <div className="absolute -bottom-1.5 right-3 w-3 h-3 rotate-45 bg-black/70" style={{ borderRight: `1px solid ${bubbleBorder}`, borderBottom: `1px solid ${bubbleBorder}` }} />
          </div>
        </div>
      )}

      {/* Isaiah character */}
      <div className="relative group isaiah-brand-star-shimmer">
        <IsaiahMascotCore
          pose={getCompanionPose()}
          size="bust"
          glow
          motionState={getCompanionMotion()}
          onClick={() => { if (starDrag.shouldSwallowClick()) return; handleClick(); }}
          testId="isaiah-companion-character"
        />
        {/* Dismiss button — hides Isaiah (mascot). The small star chat button
            stays; tapping it opens the chat and brings Isaiah back. */}
        <button
          onClick={handleDismiss}
          className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-black/80 border border-white/30 flex items-center justify-center shadow-lg hover:bg-black hover:scale-110 transition-all"
          data-testid="isaiah-companion-dismiss"
          aria-label="Hide Isaiah"
          title="Hide Isaiah (tap the star to bring him back)"
        >
          <X size={12} className="text-white/90" />
        </button>
      </div>
    </div>
  );
};

export default IsaiahFloatingCompanion;
